import { cord, cord3, formatInput, gql2 } from "@/gql/lib";
import { Subject, InvEntityType } from "../data/model";
import { fixTier3XY, correctTier12XY } from "@/gql/cords";
import {
  ActionEnum,
  AxisType,
  TimeAxisRangeInput,
  targetAxis,
} from "@/gql/types";
import { POPULATION_TYPE, PopulationTypeHelper } from "@/common/models";
import { SharedState } from "@/common/state";
import { UseModal } from "@/components/root/modal";
import EntitiesTableModalVue from "@/components/modals/entities-table-modal/EntitiesTableModal.vue";
import { EntitiesModalProps } from "@/components/modals/entities-table-modal/model";
import { TimeFrameOption } from "@/common/state/models";
import { get_entities_modal_target_axis } from "./common";

const summary_sentence = (subject: Subject) => {
  switch (subject.type) {
    case POPULATION_TYPE.EMPLOYEE:
      return `External contacts that received information from ${subject.name}`;
    case POPULATION_TYPE.FOLDER:
      return `External contacts that have access to files in ${subject.name} folder`;
    case POPULATION_TYPE.LABEL:
      return `External contacts that received ${subject.name}`;
    default:
      return "?";
  }
};

const get_summary = gql2<
  { subject: Subject; relative_time_frame: TimeFrameOption },
  { count: number; relative_count: number; type: InvEntityType; desc: string }
>(
  "inv-counters.external-contacts",
  ({ subject, relative_time_frame }, { frame }) => {
    const cords = (time_frame: TimeAxisRangeInput) =>
      correctTier12XY({
        coordinate: {
          x: cord(AxisType.ExternalReceiverType),
          y: cord(PopulationTypeHelper.asAxisType2(subject.type, "sender"), [
            subject.id,
          ]),
          z: time_frame,
        },
        action: ActionEnum.UniqCount,
      });
    return {
      query: `{
     current: tier1count(params:${formatInput(cords(frame))}){
        count
        uniqs
     }
     relative: tier1count(params:${formatInput(
       cords({
         type: relative_time_frame.type,
         values: [relative_time_frame.from],
       })
     )}){
      count
      uniqs
   }
    }`,
      mapper: (res) => {
        const { current, relative } = res;
        return {
          type: POPULATION_TYPE.CONTACT,
          count: current.length > 0 ? current[0].uniqs : 0,
          relative_count: relative.length > 0 ? relative[0].uniqs : 0,
          desc: summary_sentence(subject),
        };
      },
    };
  }
);

const get_titles = (subject: Subject) => {
  if (subject.type == POPULATION_TYPE.LABEL) {
    const label_name = subject.name.replace(/information/i, "");
    return {
      main: `<b>External Contacts</b> that received <b>${label_name}</b> information`,
      events: (name: string) =>
        `Events where <b>${name}</b> received <b>${label_name}</b> information`,
      files: (name: string) =>
        `<b>${label_name}</b> information files received by <b>${name}</b>`,
    };
  } else {
    return {
      main: `<b>External Contacts</b> that received information from <b>${subject.name}</b>`,
      events: (name: string) =>
        `Events where <b>${subject.name}</b> sent information to <b>${name}</b>`,
      files: (name: string) =>
        `Files that were sent by <b>${subject.name}</b> to <b>${name}</b>`,
    };
  }
};

const _get_main_cords = (subject: Subject) => {
  const { from, type } = SharedState.selectedTimeFrameOption.value;
  return correctTier12XY({
    coordinate: {
      x: cord(AxisType.ExternalReceiver),
      y: cord(PopulationTypeHelper.asAxisType2(subject.type, "sender"), [
        subject.id,
      ]),
      z: {
        type,
        values: [from],
      },
    },
  }).coordinate;
};

const _get_events_cords = (subject: Subject, sub_subject_id: string) => {
  const { from, type } = SharedState.selectedTimeFrameOption.value;
  return fixTier3XY({
    x: cord3(AxisType.ExternalReceiver, [sub_subject_id]),
    y: cord3(PopulationTypeHelper.asAxisType2(subject.type, "sender"), [
      subject.id,
    ]),
    z: {
      type,
      values: [from],
    },
  });
};

const show_modal = async (subject: Subject) => {
  const titles = get_titles(subject);
  const cord = _get_main_cords(subject);
  const props: EntitiesModalProps = {
    title: titles.main,
    type: POPULATION_TYPE.CONTACT,
    info_types: [],
    target_axis: get_entities_modal_target_axis(subject.id, cord),
    coordinate: cord,
    files_title: (entity) => titles.files(entity.name),
    events: {
      coordinate: (entity) => {
        return _get_events_cords(subject, entity.id);
      },
      title: (entity) => titles.events(entity.name),
    },
  };
  UseModal().show(EntitiesTableModalVue, props);
};

export default { get_summary, show_modal };
